<template>
  <div class="return-wrapper">
    <div class="blob-bg"></div>

    <div class="container py-5">
      <div class="row justify-content-center">
        <div class="col-lg-6 col-md-9">
          <div class="return-card">

            <!-- ── Loading ── -->
            <div v-if="status === 'loading'" class="state-box text-center">
              <div class="state-icon loading-icon">
                <span class="spinner-border text-primary" style="width:2.5rem;height:2.5rem;"></span>
              </div>
              <h3 class="mt-4 fw-bold">Verifying Payment...</h3>
              <p class="text-muted">Please wait while we confirm your payment with the bank.</p>
            </div>

            <!-- ── Success ── -->
            <div v-else-if="status === 'success'" class="state-box text-center">
              <div class="state-icon success-icon">
                <i class="fas fa-check-circle"></i>
              </div>
              <h2 class="mt-4 fw-bold text-success">Payment Successful!</h2>
              <p class="text-muted mb-4">ගෙවීම සාර්ථකව සිදු විය</p>

              <div class="receipt-box">
                <div class="receipt-row">
                  <span class="r-label">Invoice No.</span>
                  <span class="r-value fw-bold">{{ invoiceNumber }}</span>
                </div>
                <div class="receipt-row">
                  <span class="r-label">Account No.</span>
                  <span class="r-value">{{ accountNumber }}</span>
                </div>
                <div class="receipt-row">
                  <span class="r-label">Amount Paid</span>
                  <span class="r-value fw-bold text-success">LKR {{ formatCurrency(paymentAmount) }}</span>
                </div>
                <div v-if="remainingBalance !== null" class="receipt-row">
                  <span class="r-label">Remaining Balance</span>
                  <span class="r-value">LKR {{ formatCurrency(remainingBalance) }}</span>
                </div>
              </div>

              <p class="text-muted small mt-3 mb-4">
                <i class="fas fa-sms me-1 text-success"></i>
                An SMS confirmation has been sent to your registered mobile number.
              </p>

              <button @click="goHome" class="btn-action btn-success-action w-100">
                <i class="fas fa-home me-2"></i>Back to Home
              </button>
              <button @click="payAnother" class="btn-action btn-secondary-action w-100 mt-2">
                <i class="fas fa-redo me-2"></i>Pay Another Bill
              </button>
            </div>

            <!-- ── Failed / Cancelled ── -->
            <div v-else-if="status === 'failed'" class="state-box text-center">
              <div class="state-icon fail-icon">
                <i class="fas fa-times-circle"></i>
              </div>
              <h2 class="mt-4 fw-bold text-danger">Payment {{ cancelled ? 'Cancelled' : 'Failed' }}</h2>
              <p class="text-muted mb-4">
                {{ cancelled
                   ? 'ගෙවීම අවලංගු කරන ලදී. කිසිදු මුදලක් අය කරනු නොලැබේ.'
                   : 'ගෙවීම ව්‍යර්ථ විය. නැවත උත්සාහ කරන්න.'
                }}
              </p>
              <div v-if="errorMsg" class="error-box mb-4">
                <i class="fas fa-exclamation-triangle me-2"></i>{{ errorMsg }}
              </div>
              <button @click="tryAgain" class="btn-action btn-retry-action w-100">
                <i class="fas fa-redo me-2"></i>Try Again
              </button>
              <button @click="goHome" class="btn-action btn-secondary-action w-100 mt-2">
                <i class="fas fa-home me-2"></i>Back to Home
              </button>
            </div>

            <!-- Brand logos -->
            <div class="text-center mt-4 pt-2 border-top">
              <div class="payment-brand-row">
                <img src="../../assets/images/visa-logo-png-2017.png" alt="Visa" class="brand-logo" />
                <img src="https://upload.wikimedia.org/wikipedia/commons/2/2a/Mastercard-logo.svg" alt="Mastercard" class="brand-logo" />
              </div>
              <p class="text-muted small mt-2">
                <i class="fas fa-lock me-1"></i> Secured by Bank of Ceylon
              </p>
            </div>

          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import axios from "axios";

export default {
  name: "WaterBillPayReturn",
  data() {
    return {
      status: "loading",    // 'loading' | 'success' | 'failed'
      cancelled: false,
      invoiceNumber: "",
      accountNumber: "",
      paymentAmount: 0,
      remainingBalance: null,
      errorMsg: ""
    };
  },
  async mounted() {
    // BOC Mastercard Hosted Checkout sends these params on return:
    //   resultIndicator  – used to verify the session result with the gateway
    //   sessionVersion   – session version string
    // For cancel, BOC redirects to cancelUrl with no result params.
    const params = new URLSearchParams(window.location.search);
    const resultIndicator = params.get("resultIndicator");
    const invoiceParam    = params.get("invoice");    // we optionally pass this via return_url
    const cancelParam     = params.get("cancel");

    if (cancelParam === "1" || (!resultIndicator && !invoiceParam)) {
      // Customer cancelled or arrived with no params
      this.cancelled = true;
      this.status = "failed";
      return;
    }

    // Determine invoice number: from URL param, or from sessionStorage set before redirect
    const invoiceNumber = invoiceParam
      || sessionStorage.getItem("wbp_pending_invoice")
      || "";

    if (!invoiceNumber) {
      this.errorMsg = "Invoice reference not found. Please contact support.";
      this.status = "failed";
      return;
    }

    try {
      // Call our backend /complete endpoint.
      // gateway_status comes from BOC result: when BOC redirects with resultIndicator,
      // it means the session completed. The actual result is either SUCCESS or needs
      // server-side verification. We pass the resultIndicator as gateway_reference so
      // the backend (or boc-notify) can verify it. For now we use "success" as the
      // gateway confirmed the checkout session completed positively.
      const gatewayStatus    = resultIndicator ? "success" : "failed";
      const gatewayReference = resultIndicator || null;

      const completeRes = await axios.post("/water_bill/online-payment/complete", {
        invoice_number:    invoiceNumber,
        gateway_status:    gatewayStatus,
        gateway_reference: gatewayReference
      });

      if (completeRes?.data?.status === "success") {
        const d = completeRes.data.data || {};
        this.invoiceNumber   = invoiceNumber;
        this.accountNumber   = d.accountNumber  || "";
        this.paymentAmount   = d.paymentAmount  || 0;
        this.remainingBalance = d.remainingBalance ?? null;
        this.status = "success";
        sessionStorage.removeItem("wbp_pending_invoice");
      } else {
        throw new Error(completeRes?.data?.message || "Payment could not be finalized.");
      }
    } catch (error) {
      this.errorMsg = error?.response?.data?.message || error?.message || "Payment verification failed.";
      this.status = "failed";
      console.error("WaterBillPayReturn error:", error);
    }
  },
  methods: {
    goHome()      { this.$router.push("/"); },
    payAnother()  { this.$router.push("/water_bill_landing"); },
    tryAgain()    { this.$router.push("/water_bill_landing"); },
    formatCurrency(val) {
      return parseFloat(val || 0).toLocaleString(undefined, {
        minimumFractionDigits: 2, maximumFractionDigits: 2
      });
    }
  }
};
</script>


<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;600;800&display=swap');

.return-wrapper {
  font-family: 'Plus Jakarta Sans', sans-serif;
  background-color: #f4f7ff;
  min-height: 100vh;
  position: relative;
  overflow: hidden;
  display: flex;
  align-items: center;
}
.blob-bg {
  position: absolute; top: -10%; right: -5%;
  width: 500px; height: 500px;
  background: radial-gradient(circle, rgba(13,110,253,0.15) 0%, rgba(255,255,255,0) 70%);
  z-index: 0;
}

.return-card {
  background: rgba(255,255,255,0.95);
  backdrop-filter: blur(10px);
  border-radius: 32px;
  box-shadow: 0 40px 100px rgba(0,0,0,0.08);
  padding: 48px 40px;
  z-index: 1;
  position: relative;
}

.state-box { padding: 8px 0; }

.state-icon {
  width: 90px; height: 90px; border-radius: 50%;
  display: flex; align-items: center; justify-content: center;
  margin: 0 auto; font-size: 2.8rem;
}
.loading-icon { background: #eff6ff; }
.success-icon { background: #f0fdf4; color: #16a34a; }
.fail-icon    { background: #fef2f2; color: #dc2626; }

.receipt-box {
  background: #f8f9ff; border-radius: 18px;
  border: 1px solid #e2e8f0; padding: 16px 20px; text-align: left;
}
.receipt-row {
  display: flex; justify-content: space-between; align-items: center;
  padding: 8px 0; border-bottom: 1px solid #eef2f7;
}
.receipt-row:last-child { border-bottom: none; }
.r-label { font-size: 0.85rem; color: #64748b; font-weight: 600; text-transform: uppercase; }
.r-value { font-size: 0.95rem; color: #1a202c; }

.error-box {
  background: #fef2f2; border: 1px solid #fecaca;
  border-radius: 12px; padding: 12px 16px;
  font-size: 0.9rem; color: #991b1b;
}

.btn-action {
  border: none; border-radius: 16px;
  padding: 16px; font-size: 1rem; font-weight: 700;
  cursor: pointer; transition: all 0.2s;
}
.btn-success-action {
  background: linear-gradient(135deg, #0f766e, #0d9488);
  color: white; box-shadow: 0 8px 20px rgba(13,148,136,0.25);
}
.btn-retry-action {
  background: linear-gradient(135deg, #0d6efd, #0052cc);
  color: white; box-shadow: 0 8px 20px rgba(13,110,253,0.25);
}
.btn-secondary-action {
  background: #f1f5f9; color: #475569;
}
.btn-action:hover:not(:disabled) { transform: translateY(-2px); }

.payment-brand-row { display: flex; align-items: center; justify-content: center; gap: 25px; }
.brand-logo { height: 40px; opacity: 0.75; }

@media (max-width: 576px) {
  .return-card { padding: 32px 20px; border-radius: 24px; }
}
</style>
